import React, { useState } from 'react';
import * as yup from 'yup';
import { Form, FormikProvider, useFormik } from 'formik';
import { useAppDispatch } from 'src/store/hooks';
import { handleModalCheckEmail } from 'src/store/actions/modal';
import { H3, Paragraph } from 'src/components/reusable/Typography';
import { Button } from 'src/components/reusable/Buttons';
import {
  FormGroup,
  Input,
  InputPassword,
  InputPhone,
  Label
} from 'src/components/reusable/Forms';

interface RegisterFormProps {
  openLogin: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ openLogin }) => {
  const dispatch = useAppDispatch();
  const [code, setCode] = useState(null);

  const validationRules = yup.object().shape({
    fullname: yup
      .string()
      .min(4, 'Must be 4 characters or more')
      .required('Required'),
    email: yup.string().required('Required').email('Invalid email address'),
    phone: yup.string().required('Required'),
    password: yup
      .string()
      .required('Required')
      .min(8, 'Must be 8 characters or more')
      .matches(
        /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
        'One Uppercase, One Lowercase, One Number and one special case Character'
      )
  });

  const formik = useFormik({
    initialValues: {
      validateOnMount: true,
      fullname: '',
      email: '',
      phone: '',
      password: ''
    },
    validationSchema: validationRules,
    onSubmit: async (values) => {
      console.log(values);
      dispatch(handleModalCheckEmail(true));
    }
  });

  const { values, getFieldProps, handleSubmit, setFieldValue, isValid, dirty } =
    formik;

  return (
    <div className="signup-form py-6">
      <div className="text-left mb-10 flex flex-col gap-y-4">
        <H3>Create an account</H3>
        <div>
          <Paragraph className="text-base md:text-[18px] font-medium text-body-tertiary">
            Start sending gift cards to your loved ones.
          </Paragraph>
          <Paragraph className="text-base md:text-[18px] font-medium text-body-tertiary">
            Your location doesn’t matter
          </Paragraph>
        </div>
      </div>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          noValidate
          onSubmit={handleSubmit}
          className="grid grid-cols-1"
        >
          <FormGroup>
            <Label htmlFor="fullname">Full name</Label>
            <Input
              {...getFieldProps('fullname')}
              placeholder="eg: Agbamoro Victoria"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email address</Label>
            <Input
              type="email"
              {...getFieldProps('email')}
              placeholder="eg: Agbamoro Victoria"
            />
          </FormGroup>

          <FormGroup>
            <InputPhone
              label="Phone number"
              type="text"
              {...getFieldProps('phone')}
              handleSelectCode={(e) => setCode(e.code)}
              placeholder="Enter your phone number"
            />
          </FormGroup>

          <FormGroup>
            <InputPassword
              label="Password"
              groupClassName="input-group-merge"
              hideIcon="hide"
              showIcon="show"
              {...getFieldProps('password')}
              placeholder="Enter your password"
            />
          </FormGroup>

          <div className="w-full mb-3">
            <Label className="text-transparent" htmlFor="">
              Register
            </Label>
            <Button
              isArrow
              disabled={!(isValid && dirty)}
              className="w-full text-center"
              arrowClassName="hidden xl:block absolute right-20"
              type="submit"
            >
              Create account
            </Button>
          </div>

          <div className="w-full text-base">
            Already have an account?{' '}
            <span
              className="text-primary font-bold cursor-pointer"
              onClick={openLogin}
            >
              Login
            </span>
          </div>
        </Form>
      </FormikProvider>
    </div>
  );
};

export default RegisterForm;
